const registerMiramar = () => {
  // First-party domain first: herokuapp.com is blocked by some corporate
  // filters/ad-blockers (caused the trade-member incident). Old URL kept as
  // automatic fallback.
  const API_URLS = [
    "https://mto.milagrocollective.com",
    "https://milagro-custom-order-variants-b5b7817a8c17.herokuapp.com",
  ];
  Alpine.data("MiramarForm", () => ({
    flags: {
      appLoaded: false,
      apiReady: false,
      apiChecking: false,
      addRugPad: false,
      is_wholesale: false,
      debug: false,
      adding: false,
    },
    api_url: null,
    $product: null,
    product: null, // to be set from data attribute
    rug_pad_product: null, // to be set from data attribute
    config: {
      prefix: "Made To Order",
      width: {
        ft: [
          3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
        ],
        inch: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
      },
      length: {
        ft: Array.from({ length: 46 }, (_, i) => i + 5),
        inch: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
      },
      price_per_sqft: 0, // to be set from data attribute
      rug_pad_price: 0, // to be set from data attribute,
      freight_rate_cents: 500,
      freight_threshold_sqft: 180,
      wp_discount_percent: 0, // to be set from data attribute
    },
    selected: {
      width: { ft: 3, inch: 0 },
      length: { ft: 5, inch: 0 },
      price: 0,
      rug_pad_price: 0,
      freight_charge: 0,
    },
    colorway: 'Dusty Blue',
    direction: 'Original',
    colors: [],
    previewReady: false,
    previewError: '',
    display_price: 0,
    compare_price: 0,
    order_meta: {
      is_standard_size: false,
      lead_time: "",
      shipping_status: "",
      order_type: "",
    },
    u_IsDebugEnabled() {
      return new URLSearchParams(window.location.search).get("mto_debug") === "true";
    },
    u_Debug(...args) {
      if (!this.flags.debug) return;
      console.log("[MTO]", ...args);
    },
    u_ParseJsonValue(value, label) {
      if (!value) {
        this.u_Debug(`${label}:missing`);
        return null;
      }

      try {
        return JSON.parse(value);
      } catch (error) {
        console.error(`[MTO] Invalid ${label} JSON`, error);
        this.u_Debug(`${label}:invalid-json`, {
          value,
          message: error.message,
        });
        return null;
      }
    },
    u_RequireNode(node, label) {
      if (!node) {
        throw new Error(`Missing required element: ${label}`);
      }

      return node;
    },
    u_Wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    u_ShowUserError(message, error) {
      console.error("[MTO]", message, error);
      this.u_Debug("user-error", {
        message,
        errorMessage: error?.message,
      });
      this.u_ReportError(message, error);

      if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert(message);
      }
    },
    // Tell the shopper something without also filing it as an error; used
    // when the rug went in but the pad did not, which is already reported.
    u_Notify(message) {
      console.warn("[MTO]", message);
      if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert(message);
      }
    },
    // Add-to-cart failures are caught and shown to the shopper, so they never
    // reach Sentry as unhandled errors. Without this, a customer who cannot
    // add a rug pad leaves no trace anywhere except their own console.
    u_ReportError(message, error) {
      try {
        if (!window.Sentry || typeof window.Sentry.captureException !== "function") return;
        window.Sentry.captureException(
          error instanceof Error ? error : new Error(message),
          {
            tags: {
              mto_step: "add-to-cart",
              mto_add_rug_pad: String(this.flags.addRugPad),
            },
            extra: {
              message,
              productHandle: this.product?.handle,
              productId: this.product?.id,
              rugPadHandle: this.rug_pad_product?.handle,
              rugPadId: this.rug_pad_product?.id,
              selectedSize: `${this.selected.width.ft}ft ${this.selected.width.inch}in x ${this.selected.length.ft}ft ${this.selected.length.inch}in`,
              isWholesale: this.flags.is_wholesale,
              apiUrl: this.api_url,
              detail: error?.mtoDetail ?? null,
            },
          },
        );
      } catch (reportingError) {
        // Reporting must never break the shopper's flow.
        console.error("[MTO] Sentry reporting failed", reportingError);
      }
    },
    async u_FindVariantIdByHandle(handle, barcode, variantType, maxAttempts = 10) {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        this.u_Debug(`${variantType}:lookup-attempt`, {
          attempt,
          handle,
          barcode,
        });

        let response = await fetch(
          `/products/${handle}?section_id=get-product-variant&ts=${Date.now()}`,
        );
        let responseText = await response.text();
        let parser = new DOMParser();
        let parsedHTML = parser.parseFromString(responseText, "text/html");
        let variantNode = parsedHTML.querySelector("div[data-variant_id]");
        let variantId = variantNode?.dataset.variant_id || null;

        this.u_Debug(`${variantType}:lookup-result`, {
          attempt,
          ok: response.ok,
          status: response.status,
          variantId,
        });

        if (variantId) {
          return variantId;
        }

        if (attempt < maxAttempts) {
          await this.u_Wait(500);
        }
      }

      return null;
    },
    u_NormalizeVariantId(variantId) {
      if (!variantId) {
        return null;
      }

      let variantIdString = String(variantId);
      if (variantIdString.startsWith("gid://shopify/ProductVariant/")) {
        return variantIdString.split("/").pop() || null;
      }

      return variantIdString;
    },
    async u_FindVariantByBarcode(handle, barcode, maxAttempts = 5) {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          let res = await fetch(`/products/${handle}.js?ts=${Date.now()}`);
          if (!res.ok) continue;
          let productData = await res.json();
          let variant = productData?.variants?.find(
            (v) => v.barcode === barcode,
          );
          if (variant) {
            this.u_Debug("u_FindVariantByBarcode:found", {
              attempt,
              variantId: variant.id,
              barcode: variant.barcode,
            });
            return String(variant.id);
          }
          this.u_Debug("u_FindVariantByBarcode:not-found-yet", { attempt });
        } catch (e) {
          this.u_Debug("u_FindVariantByBarcode:error", { attempt, message: e.message });
        }
        if (attempt < maxAttempts) await this.u_Wait(1000);
      }
      return null;
    },
    async u_VerifyMtoVariant(handle, variantId, expectedBarcode, expectedTitle) {
      try {
        let res = await fetch(`/products/${handle}.js?ts=${Date.now()}`);
        if (!res.ok) return true; // can't verify, trust the API
        let productData = await res.json();
        let variant = productData?.variants?.find(
          (v) => String(v.id) === String(variantId),
        );
        if (!variant) return true; // variant not in storefront yet, trust the API

        this.u_Debug("u_VerifyMtoVariant:check", {
          variantId,
          variantTitle: variant.title,
          variantBarcode: variant.barcode,
          expectedTitle,
          expectedBarcode,
        });

        // Check title matches what we expect (the MTO dimension string)
        if (expectedTitle && variant.title !== expectedTitle) {
          this.u_Debug("u_VerifyMtoVariant:title-mismatch", {
            variantId,
            variantTitle: variant.title,
            expectedTitle,
          });
          return false;
        }

        return true;
      } catch (e) {
        this.u_Debug("u_VerifyMtoVariant:error", { message: e.message });
        return true; // can't verify, trust the API
      }
    },
    u_GetProductOptionName(product) {
      this.u_Debug("u_GetProductOptionName:input", {
        hasOptions: !!product?.options,
        isArray: Array.isArray(product?.options),
        optionsLength: product?.options?.length,
        options: product?.options
      });

      // Try to find Size option first
      if (product?.options && Array.isArray(product.options)) {
        // Shopify product JSON from Liquid gives options as strings (e.g. ["Title", "Color"])
        // while the Admin API gives objects with a .name property
        let getOptionName = (opt) => typeof opt === 'string' ? opt : opt?.name;

        let sizeOption = product.options.find(opt =>
          getOptionName(opt)?.toLowerCase() === 'size'
        );
        if (sizeOption) {
          let name = getOptionName(sizeOption);
          this.u_Debug("u_GetProductOptionName:found-size", {
            optionName: name
          });
          return name;
        }

        // Fallback to first option
        let firstName = getOptionName(product.options[0]);
        if (product.options.length > 0 && firstName) {
          this.u_Debug("u_GetProductOptionName:fallback-to-first", {
            optionName: firstName
          });
          return firstName;
        }
      }

      // Default fallback - this means the product has NO options configured
      this.u_Debug("u_GetProductOptionName:using-default", {
        optionName: "Size",
        reason: "Product has no options configured in Shopify"
      });
      return "Size";
    },
    async u_CheckApiStatus(maxAttempts = 2) {
      // Retry with backoff: a single failed check (e.g. during Heroku's daily
      // dyno restart) would otherwise hide the entire MTO form until reload.
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        for (let url of API_URLS) {
          try {
            let options = { signal: AbortSignal.timeout(8000) };
            let api_res = await fetch(
              `${url}/api-status?ts=${Date.now()}`,
              options,
            );
            if (api_res.ok) {
              this.api_url = url;
              this.u_Debug("api-status:ok", { attempt, url });
              return true;
            }
            this.u_Debug("api-status:not-ok", {
              attempt,
              url,
              status: api_res.status,
            });
          } catch (error) {
            this.u_Debug("api-status:error", {
              attempt,
              url,
              message: error.message,
            });
          }
        }
        if (attempt < maxAttempts) {
          await this.u_Wait(1000 * Math.pow(2, attempt - 1));
        }
      }
      return false;
    },
    async u_RefreshApiStatus() {
      if (this.flags.apiChecking) return;
      this.flags.apiChecking = true;
      try {
        this.flags.apiReady = await this.u_CheckApiStatus();
      } finally {
        this.flags.apiChecking = false;
      }
    },
    async init() {
      this.flags.debug = this.u_IsDebugEnabled();
      this.flags.appLoaded = true;
      this.InitWatchers();
      this.InitSelf();
      await this.u_RefreshApiStatus();
    },
    InitSelf() {
      this.product = this.u_ParseJsonValue(this.$el.dataset.product, "product");
      this.config.price_per_sqft =
        parseFloat(this.$el.dataset.price_per_sqft) || 0;

      this.rug_pad_product = this.u_ParseJsonValue(
        this.$el.dataset.rug_pad_product,
        "rug_pad_product",
      );
      this.config.rug_pad_price =
        parseFloat(this.$el.dataset.rug_pad_price) || 0;

      this.selected.width.ft = this.config.width.ft[0];
      this.selected.length.ft = this.config.length.ft[0];

      // wholesale settings
      this.flags.is_wholesale = this.$el.dataset.is_wholesale === "true";
      this.config.wp_discount_percent =
        parseFloat(this.$el.dataset.wholesale_discount) || 0;

      let $product = this.$el.closest("product-info");
      this.$product = $product;
      if (!this.product?.id || !this.product?.handle) {
        console.error("[MTO] Missing required product dataset", this.$el.dataset.product);
      }
      if (!this.product?.options || !Array.isArray(this.product.options) || this.product.options.length === 0) {
        console.warn("[MTO] Product options array is missing or empty. Make sure product JSON includes 'options' field.");
      }
      if (this.rug_pad_product && (!this.rug_pad_product?.options || !Array.isArray(this.rug_pad_product.options) || this.rug_pad_product.options.length === 0)) {
        console.warn("[MTO] Rug pad product options array is missing or empty. Make sure rug pad product JSON includes 'options' field.");
      }
      if (this.flags.is_wholesale && !this.$el.dataset.wholesale_discount) {
        console.warn("[MTO] Wholesale display is enabled but wholesale discount data is missing");
      }
      if (this.flags.is_wholesale) {
        let $priceElement = $product?.querySelector("[data-mto-price]") || $product?.querySelector("[data-wpd-variant-price]");
        if (!$priceElement) {
          console.warn("[MTO] Wholesale display is enabled but price element [data-mto-price] or [data-wpd-variant-price] is missing");
        }
      }
      this.u_Debug("InitSelf:complete", {
        productId: this.product?.id,
        productHandle: this.product?.handle,
        productOptions: this.product?.options,
        rugPadProductId: this.rug_pad_product?.id,
        rugPadHandle: this.rug_pad_product?.handle,
        rugPadOptions: this.rug_pad_product?.options,
        pricePerSqft: this.config.price_per_sqft,
        rugPadPrice: this.config.rug_pad_price,
        isWholesale: this.flags.is_wholesale,
        wholesaleDiscount: this.config.wp_discount_percent,
      });
      this.colors = JSON.parse(this.$el.dataset.colors);
      this.$watch('colorway', () => this.u_DrawPreview());
      this.$watch('direction', () => this.u_DrawPreview());
      this.$nextTick(() => { this.u_InitPreview(); this.u_InitPoms(); });
      this.u_UpdatePrice();

      // Move MTO price into the original price block's position
      this.$nextTick(() => {
        let $mtoPriceEl = this.$el.querySelector(".mto-calculated-price");
        let $themePriceBlock = $product?.querySelector(".f-product-single__block--price");
        if ($mtoPriceEl && $themePriceBlock) {
          // This moves x-text bindings outside their x-data root. Alpine's
          // MutationObserver would treat that as new markup and re-initialize
          // it with no component scope, so the bindings evaluate against
          // nothing ("u_FormatMoney is not defined"). mutateDom suspends the
          // observer, leaving the existing bindings and their scope intact.
          const move = () => {
            $themePriceBlock.innerHTML = "";
            $themePriceBlock.appendChild($mtoPriceEl);
            $themePriceBlock.style.display = "";
          };
          if (window.Alpine && typeof Alpine.mutateDom === "function") {
            Alpine.mutateDom(move);
          } else {
            move();
          }
          this.u_Debug("InitSelf:price-moved-to-theme-block");
        }
      });
    },
    InitWatchers() {
      // calculate price when dimensions change
      try {
        this.$watch("selected", this.u_UpdatePrice.bind(this));
        this.$watch("flags.addRugPad", this.u_UpdatePrice.bind(this));
      } catch (e) {
        // $watch evaluates against the Alpine scope; if this component was
        // torn down between the connectivity check and here (or the shopper
        // is on a stale cached bundle), the scope is gone — watchers would
        // be useless anyway, so stop instead of throwing.
        this.u_Debug("InitWatchers:failed", { error: String(e) });
        return;
      }
      this.u_Debug("InitWatchers:registered");

      // Re-apply MTO price after WPD app processes (it adds 'wpd-processed' class)
      if (this.flags.is_wholesale) {
        const priceObserver = new MutationObserver((mutations) => {
          for (let mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              let target = mutation.target;
              if (target.classList.contains('wpd-processed')) {
                this.u_Debug("InitWatchers:wpd-detected", {
                  message: "WPD app processed the price element, reapplying MTO price"
                });
                // WPD just processed this element, reapply our MTO price
                setTimeout(() => {
                  this.u_UpdatePrice();
                }, 100);
                // Stop observing after first WPD processing
                priceObserver.disconnect();
              }
            }
          }
        });

        // Find the price element and observe it for WPD processing
        let $product = this.$el.closest("product-info");
        if ($product) {
          let $price = $product.querySelector("[data-wpd-variant-price]");
          if ($price) {
            priceObserver.observe($price, {
              attributes: true,
              attributeFilter: ['class']
            });
            this.u_Debug("InitWatchers:observing-wpd", {
              message: "Watching for WPD app to process price element"
            });
          }
        }
      }

      window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        async (state) => {
          this.u_Debug("cartUpdate:event", state);
          let t = setInterval(() => {
            document
              .querySelectorAll("cart-drawer-items .f-cart-item")
              .forEach(($item) => {
                this.u_Debug("cartUpdate:item", {
                  title: $item.dataset.cartItemProductTitle,
                });
                if ($item.dataset.cartItemProductTitle == "") {
                  this.u_Debug("cartUpdate:refresh-drawer");
                  this.RefreshCartItemsInDrawer();
                } else {
                  clearInterval(t);
                  this.u_Debug("cartUpdate:stop-refresh-loop");
                }
              });
          }, 1000);
        },
      );
    },

    u_UpdatePrice() {
      // Calculate square footage
      let sqft =
        (parseFloat(this.selected.width.ft) +
          parseFloat(this.selected.width.inch) / 12) *
        (parseFloat(this.selected.length.ft) +
          parseFloat(this.selected.length.inch) / 12);
      sqft = Math.ceil(sqft * 100) / 100; // round up to 2 decimal places

      // Keep lead time, shipping, and custom-order messaging in sync with size.
      // Specialty templates with their own shorter lead times are preserved.
      const widthInches =
        Number(this.selected.width.ft) * 12 + Number(this.selected.width.inch);
      const lengthInches =
        Number(this.selected.length.ft) * 12 + Number(this.selected.length.inch);
      const standardSizes = new Set([
        "36x60",   // 3x5
        "48x72",   // 4x6
        "60x84",   // 5x7
        "72x108",  // 6x9
        "96x120",  // 8x10
        "108x144", // 9x12
        "120x168", // 10x14
        "144x180", // 12x15
      ]);
      const isStandardSize = standardSizes.has(`${widthInches}x${lengthInches}`);
      const calculatedLeadTimeText = "Estimated time to ship: 120–150 days.";
      const isOversized = sqft > this.config.freight_threshold_sqft;
      const shippingStatus = isOversized
        ? "Freight shipping applies"
        : "Free shipping";

      const $productForLeadTime = this.$product || this.$el.closest("product-info");
      let effectiveLeadTimeText = calculatedLeadTimeText;

      $productForLeadTime?.querySelectorAll("[data-product-inventory]").forEach(($leadTime) => {
        const originalText =
          $leadTime.dataset.mtoOriginalLeadTime || $leadTime.textContent.trim();
        if (!$leadTime.dataset.mtoOriginalLeadTime) {
          $leadTime.dataset.mtoOriginalLeadTime = originalText;
        }

        if (/90\s*[-–]\s*120\s*days/i.test(originalText)) {
          $leadTime.textContent = calculatedLeadTimeText;
          effectiveLeadTimeText = calculatedLeadTimeText;
        } else if (originalText) {
          effectiveLeadTimeText = originalText;
        }
      });

      this.order_meta.is_standard_size = false;
      this.order_meta.lead_time = effectiveLeadTimeText
        .replace(/^Estimated time to ship:\s*/i, "")
        .replace(/\.$/, "");
      this.order_meta.shipping_status = shippingStatus;
      this.order_meta.order_type = "Made to order — Two Hues custom rug";

      const $fulfillmentDetails =
        $productForLeadTime?.querySelector("[data-mto-fulfillment-details]");
      if ($fulfillmentDetails) {
        $fulfillmentDetails.hidden = false;

        const $shipping = $fulfillmentDetails.querySelector("[data-mto-shipping-status]");
        if ($shipping) $shipping.textContent = shippingStatus;

        const $customPolicy =
          $fulfillmentDetails.querySelector("[data-mto-custom-policy]");
        if ($customPolicy) $customPolicy.hidden = false;
      }

      // Calculate main price (full price, no discount)
      let price = sqft * this.config.price_per_sqft;
      const freight_charge = isOversized
        ? Math.ceil(sqft * this.config.freight_rate_cents)
        : 0;
      this.selected.freight_charge = freight_charge;
      price += freight_charge;
      let compare_price = price;

      // Add rug pad price if selected
      let rug_pad_price = sqft * this.config.rug_pad_price;
      this.selected.rug_pad_price = Math.ceil(rug_pad_price); // Already in cents
      if (this.flags.addRugPad) {
        price += rug_pad_price;
      }

      // Store the FULL price (before wholesale discount) - this is what goes to cart
      this.selected.price = Math.ceil(price); // Already in cents

      // Calculate display price (with wholesale discount if applicable)
      this.display_price = price;
      this.compare_price = compare_price;
      if (this.flags.is_wholesale) {
        this.display_price = price * (1 - this.config.wp_discount_percent / 100);
      }

      this.u_Debug("u_UpdatePrice", {
        width: this.selected.width,
        length: this.selected.length,
        addRugPad: this.flags.addRugPad,
        isWholesale: this.flags.is_wholesale,
        sqft,
        comparePrice: compare_price,
        fullPrice: price,
        displayPrice: this.display_price,
        selectedPrice: this.selected.price,
        selectedRugPadPrice: this.selected.rug_pad_price,
      });

      return this.display_price;
    },

    async u_RequireMatchingVariant(handle, variantId, expectedTitle, expectedPrice) {
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          const response = await fetch(`/products/${handle}?variant=${encodeURIComponent(variantId)}&section_id=mto-variant-check&ts=${Date.now()}`, { cache: "no-store" });
          if (response.ok) {
            const html = new DOMParser().parseFromString(await response.text(), "text/html");
            const variant = JSON.parse(html.querySelector('[data-mto-variant-check]')?.textContent || "null");
            if (variant && String(variant.id) === String(variantId) && variant.title === expectedTitle && Number(variant.price) === Number(expectedPrice)) return;
          }
        } catch (error) {
          this.u_Debug("variant-price:verification-retry", { message: error.message });
        }
        if (attempt < 5) await this.u_Wait(1000);
      }
      throw new Error("We couldn’t confirm the price for your selected size. Nothing further was added. Please refresh and try again, or contact us for help.");
    },
    async e_AddCustomItemsToCart(e) {
      // Concurrent runs race each other (duplicate cart adds, Shopify cart
      // throttling on the second run's requests) — ignore clicks mid-flight.
      if (this.flags.adding) {
        this.u_Debug("e_AddCustomItemsToCart:ignored-while-in-flight");
        return;
      }
      if (!this.flags.apiReady) {
        this.u_ShowUserError("Ordering is temporarily unavailable. Please use Retry below or contact us for help.");
        return;
      }
      this.u_UpdatePrice();
      const selection = JSON.parse(JSON.stringify(this.selected));
      const addRugPad = this.flags.addRugPad;
      const colorway = this.colorway;
      const direction = this.direction;
      const controls = Array.from(this.$el.closest('product-info').querySelectorAll('select, input:not([type="hidden"]), button')).filter(node => !node.closest('.dummy-product-form'));
      const disabledStates = controls.map(node => node.disabled);
      controls.forEach(node => { node.disabled = true; });
      this.flags.adding = true;
      let $btn = e.currentTarget;
      $btn.classList.add("btn--loading");
      this.u_Debug("e_AddCustomItemsToCart:start", {
        addRugPad: addRugPad,
        selected: selection,
      });

      try {
        let $product = this.u_RequireNode(
          this.$el.closest("product-info"),
          "product-info",
        );
        let $product_form = this.u_RequireNode(
          $product.querySelector(".dummy-product-form"),
          ".dummy-product-form",
        );
        const propertyValues = Array.from($product_form.querySelectorAll('[name^="properties["]')).map(node => [node, node.value]);
        let $main_select = this.u_RequireNode(
          $product_form.querySelector('[name="id"]'),
          '[name="id"]',
        );
        let $button_main = this.u_RequireNode(
          $product_form.querySelector('button[name="add"]'),
          'button[name="add"]',
        );

        const widthInches = Number(selection.width.ft) * 12 + Number(selection.width.inch);
        const lengthInches = Number(selection.length.ft) * 12 + Number(selection.length.inch);
        if (widthInches < 36 || widthInches > 300 || lengthInches < 60 || lengthInches > 600) {
          throw new Error("Please select a size between 3′ x 5′ and 25′ x 50′.");
        }
        if (!this.product?.id || !this.product?.handle) {
          throw new Error("Missing main product data");
        }

        if (
          addRugPad &&
          (!this.rug_pad_product?.id || !this.rug_pad_product?.handle)
        ) {
          throw new Error("Missing rug pad product data");
        }

        // Oversized rugs get a distinct, visually-identical option value so Shopify
        // can never reuse a legacy no-freight custom variant at the old price.
        const freightVariantMarker = selection.freight_charge > 0 ? "\u2063" : "";
        let variant_title = `${selection.width.ft}′ ${selection.width.inch}″ x ${selection.length.ft}′ ${selection.length.inch}″ — ${colorway} / ${direction}${freightVariantMarker}`;
        let variant_barcode = `${this.product.id}-${variant_title}-${selection.freight_charge > 0 ? "FREIGHT-" : ""}MM`;

        let attribute = {
          "_mto-variant-barcode": variant_barcode,
        };
        let cartUpdateResponse = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            attributes: attribute,
          }),
        });
        this.u_Debug("cart:update-attributes", {
          attribute,
          ok: cartUpdateResponse.ok,
          status: cartUpdateResponse.status,
        });
        if (!cartUpdateResponse.ok) {
          throw new Error("Failed to store MTO cart attributes");
        }

        let payload = {
          shop_name: Shopify.shop,
        };

        // Recalculate rug price at click time so oversized freight cannot be dropped.
        const checkoutSqft = Math.ceil(
          ((Number(selection.width.ft) + Number(selection.width.inch) / 12) *
            (Number(selection.length.ft) + Number(selection.length.inch) / 12)) * 100
        ) / 100;
        const checkoutFreight = checkoutSqft > this.config.freight_threshold_sqft
          ? Math.ceil(checkoutSqft * this.config.freight_rate_cents)
          : 0;
        selection.freight_charge = checkoutFreight;
        let main_product_price = Math.ceil(
          checkoutSqft * this.config.price_per_sqft + checkoutFreight
        );

        let mainProductOptionName = this.u_GetProductOptionName(this.product);
        this.u_Debug("create-rug-variants:main-option-name", {
          optionName: mainProductOptionName
        });

        payload.main_product = {
          id: this.product.id,
          variant: {
            price: (main_product_price / 100).toFixed(2),
            barcode: variant_barcode,
            optionValues: [
              {
                optionName: mainProductOptionName,
                name: variant_title,
              },
            ],
          },
        };

        if (addRugPad) {
          let rugPadOptionName = this.u_GetProductOptionName(this.rug_pad_product);
          this.u_Debug("create-rug-variants:rugpad-option-name", {
            optionName: rugPadOptionName
          });

          payload.rugpad_product = {
            id: this.rug_pad_product.id,
            variant: {
              price: (selection.rug_pad_price / 100).toFixed(2),
              barcode: variant_barcode,
              optionValues: [
                {
                  optionName: rugPadOptionName,
                  name: variant_title,
                },
              ],
            },
          };
        }
        this.u_Debug("create-rug-variants:payload", payload);

        let api_base = this.api_url || API_URLS[0];
        let createResponse = await fetch(`${api_base}/api/create-rug-variants`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ payload }),
        });
        let api_response = await createResponse.json().catch(() => null);

        this.u_Debug("create-rug-variants:response", {
          ok: createResponse.ok,
          status: createResponse.status,
          body: api_response,
        });

        if (api_response?.userErrors) {
          console.error("[MTO] create-rug-variants:userErrors", api_response.userErrors);
        }

        if (!createResponse.ok) {
          throw new Error(
            api_response?.error || `Variant API request failed (${createResponse.status})`,
          );
        }

        if (api_response?.error) {
          let errorMessage = api_response.error;
          if (api_response?.userErrors && Array.isArray(api_response.userErrors) && api_response.userErrors.length > 0) {
            let userErrorDetails = api_response.userErrors.map(err => {
              return `${err.field || 'unknown'}: ${err.message || 'unknown error'}`;
            }).join('; ');
            errorMessage = `${api_response.error} - Details: ${userErrorDetails}`;
          }
          throw new Error(errorMessage);
        }

        let api_main_variant_id = this.u_NormalizeVariantId(
          api_response?.MainVariant?.productVariants?.[0]?.id,
        );
        let api_rug_pad_variant_id = this.u_NormalizeVariantId(
          api_response?.RugpadVariant?.productVariants?.[0]?.id,
        );
        this.u_Debug("create-rug-variants:normalized-variant-ids", {
          mainVariantId: api_main_variant_id,
          rugPadVariantId: api_rug_pad_variant_id,
        });

        // Verify the API-returned main variant is actually the MTO variant, not the default
        // Fetch product JSON and check the variant's barcode matches
        if (api_main_variant_id) {
          let verified = await this.u_VerifyMtoVariant(
            this.product.handle,
            api_main_variant_id,
            variant_barcode,
            variant_title,
          );
          if (!verified) {
            this.u_Debug("create-rug-variants:main-variant-is-default", {
              apiVariantId: api_main_variant_id,
              expectedBarcode: variant_barcode,
            });
            api_main_variant_id = null; // force fallback to lookup
          }
        }

        await new Promise((resolve, reject) => {
          this.$nextTick(async () => {
            let rug_pad_error = null;
            try {
              this.u_Debug("nextTick:start", {
                addRugPad: addRugPad,
              });

              if (addRugPad) {
                try {
                  let rug_pad_variant_id = api_rug_pad_variant_id;
                  this.u_Debug("rug-pad:variant-id-source", {
                    source: rug_pad_variant_id ? "api" : "lookup",
                    variantId: rug_pad_variant_id,
                  });

                  if (!rug_pad_variant_id) {
                    rug_pad_variant_id = await this.u_FindVariantIdByHandle(
                      this.rug_pad_product.handle,
                      variant_barcode,
                      "rug-pad",
                    );
                  }

                  if (!rug_pad_variant_id) {
                    throw new Error("Unable to find rug pad variant after creation");
                  }

                  await this.u_RequireMatchingVariant(this.rug_pad_product.handle, rug_pad_variant_id, variant_title, selection.rug_pad_price);
                  let rugPadCartResponse = null;
                  let rugPadCartBody = null;
                  // The rug pad variant is often created seconds earlier by the
                  // MTO API, and Shopify does not expose it to /cart/add.js
                  // instantly. Three attempts over 1.5s was not enough headroom;
                  // the main product already gets ~5s of lookup retries.
                  let rugPadMaxAttempts = 6;
                  for (let attempt = 1; attempt <= rugPadMaxAttempts; attempt++) {
                    rugPadCartResponse = await fetch("/cart/add.js", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        id: rug_pad_variant_id,
                        quantity: 1,
                        properties: {
                          "_is-MTO": "true",
                        },
                      }),
                    });
                    rugPadCartBody = await rugPadCartResponse.json().catch(() => null);

                    this.u_Debug("rug-pad:cart-add-response", {
                      attempt,
                      ok: rugPadCartResponse.ok,
                      status: rugPadCartResponse.status,
                      variantId: rug_pad_variant_id,
                      body: rugPadCartBody,
                    });

                    if (rugPadCartResponse.ok && rugPadCartBody?.id) {
                      break;
                    }

                    if (attempt < rugPadMaxAttempts) {
                      this.u_Debug("rug-pad:cart-add-retry", {
                        attempt,
                        nextAttempt: attempt + 1,
                        status: rugPadCartResponse.status,
                      });
                      await this.u_Wait(attempt * 500);
                    }
                  }

                  if (!rugPadCartResponse?.ok || !rugPadCartBody?.id) {
                    // Keep the shopper-facing text clean, but carry Shopify's
                    // own rejection reason through to Sentry — it is the only
                    // thing that separates "variant does not exist" from
                    // "sold out" from a transient failure.
                    let cartError = new Error("Unable to add rug pad to cart");
                    cartError.mtoDetail = {
                      status: rugPadCartResponse?.status ?? null,
                      description:
                        rugPadCartBody?.description || rugPadCartBody?.message || null,
                      variantId: rug_pad_variant_id,
                      variantIdSource: api_rug_pad_variant_id ? "api" : "lookup",
                      attempts: rugPadMaxAttempts,
                    };
                    throw cartError;
                  }
                } catch (padError) {
                  // A pad that will not go in must not cost the shopper their
                  // rug: record it, report it, and carry on with the main add.
                  rug_pad_error = padError;
                  this.u_Debug("rug-pad:failed-continuing-with-rug", {
                    message: padError.message,
                    detail: padError.mtoDetail ?? null,
                  });
                  this.u_ReportError("Unable to add rug pad to cart", padError);
                }
              }

              let main_variant_id = api_main_variant_id;
              this.u_Debug("main:variant-id-source", {
                source: main_variant_id ? "api" : "lookup",
                variantId: main_variant_id,
              });

              if (!main_variant_id) {
                // Try finding variant by barcode from product JSON
                main_variant_id = await this.u_FindVariantByBarcode(
                  this.product.handle,
                  variant_barcode,
                );
                this.u_Debug("main:variant-id-from-barcode-lookup", {
                  variantId: main_variant_id,
                });
              }

              if (!main_variant_id) {
                main_variant_id = await this.u_FindVariantIdByHandle(
                  this.product.handle,
                  variant_barcode,
                  "main",
                );
              }

              if (!main_variant_id) {
                throw new Error("Unable to find main variant after creation");
              }

              await this.u_RequireMatchingVariant(this.product.handle, main_variant_id, variant_title, main_product_price);
              propertyValues.forEach(([node, value]) => { node.value = value; });
              $main_select.value = main_variant_id;
              $main_select.dispatchEvent(new Event("change", { bubbles: true }));
              this.u_Debug("main:select-updated", {
                variantId: main_variant_id,
              });
              const nativeForm = $button_main.closest('product-form');
              if (!nativeForm?.onSubmitHandler) throw new Error("Unable to prepare your cart. Please refresh and try again.");
              const added = await nativeForm.onSubmitHandler({ preventDefault() {} });
              if (!added || nativeForm.error) throw new Error("Your rug could not be added. Please check your cart before trying again.");
              this.u_Debug("main:add-clicked", {
                variantId: main_variant_id,
              });

              this.$nextTick(() => {
                this.u_Debug("cart-drawer:refresh-requested-after-add");
                this.RefreshCartItemsInDrawer();
              });

              if (rug_pad_error) {
                this.u_Notify(
                  "Your rug was added to the cart, but the rug pad could not be added. Please contact us and we'll add it to your order.",
                );
              }

              resolve();
            } catch (error) {
              reject(error);
            }
          });
        });
      } catch (error) {
        this.u_Debug("e_AddCustomItemsToCart:error", {
          message: error.message,
          stack: error.stack,
        });
        this.u_ShowUserError(
          error.message || "Unable to add custom item to cart. Please try again.",
          error,
        );
      } finally {
        controls.forEach((node, index) => { node.disabled = disabledStates[index]; });
        this.flags.adding = false;
        $btn.classList.remove("btn--loading");
        this.u_Debug("e_AddCustomItemsToCart:complete");
      }
    },

    async RefreshCartItemsInDrawer() {
      this.u_Debug("RefreshCartItemsInDrawer:start");
      let cart_drawer = await fetch(
        `/?section_id=cart-drawer&ts=${Date.now()}`,
      ).then((res) => res.text());
      let parser = new DOMParser();
      let parsedHTML = parser.parseFromString(cart_drawer, "text/html");
      let newCartDrawerItems = parsedHTML.querySelector("cart-drawer-items");
      this.u_Debug("RefreshCartItemsInDrawer:parsed", {
        hasItems: !!newCartDrawerItems,
      });
      if (!newCartDrawerItems) {
        return;
      }
      document.querySelectorAll("cart-drawer-items").forEach(($item) => {
        $item.replaceWith(newCartDrawerItems);
      });
      this.u_Debug("RefreshCartItemsInDrawer:complete");
    },


    async u_InitPoms() {
      const photo = new Image();
      photo.crossOrigin = 'anonymous';
      try {
        await new Promise((resolve, reject) => {
          photo.onload = resolve;
          photo.onerror = reject;
          photo.src = this.$el.dataset.pom;
        });
        const buffer = document.createElement('canvas');
        buffer.width = 128;
        buffer.height = 128;
        const context = buffer.getContext('2d');
        context.drawImage(photo, 0, 0, 128, 128);
        const source = context.getImageData(0, 0, 128, 128);
        this.$el.querySelectorAll('.pt-pom').forEach(canvas => {
          const rgb = canvas.dataset.color.match(/[a-f0-9]{2}/gi).map(value => parseInt(value, 16));
          const output = new ImageData(new Uint8ClampedArray(source.data), 128, 128);
          for (let i = 0; i < output.data.length; i += 4) {
            const light = (source.data[i] + source.data[i + 1] + source.data[i + 2]) / 3;
            const texture = light / 205;
            for (let channel = 0; channel < 3; channel++) output.data[i + channel] = rgb[channel] * texture;
          }
          canvas.getContext('2d').putImageData(output, 0, 0);
          canvas.style.background = 'transparent';
        });
      } catch (error) {
        console.warn('Wool color swatches could not load', error);
      }
    },

    async u_InitPreview() {
      const canvas = this.$product.querySelector('[data-pt-canvas]');
      const photo = new Image();
      photo.crossOrigin = 'anonymous';
      try {
        await new Promise((resolve, reject) => {
          photo.onload = resolve;
          photo.onerror = reject;
          photo.src = this.$el.dataset.preview;
        });
        canvas.width = photo.naturalWidth;
        canvas.height = photo.naturalHeight;
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        const scale = .953;
        context.drawImage(photo, canvas.width * (1 - scale) / 2, canvas.height * (1 - scale) / 2 - canvas.height * .004, canvas.width * scale, canvas.height * scale);
        this._previewSource = context.getImageData(0, 0, canvas.width, canvas.height);
        this._previewCanvas = canvas;
        this.previewReady = true;
        this.u_DrawPreview();
      } catch (error) {
        this.previewError = 'The live preview could not load. Please refresh the page.';
      }
    },
    u_DrawPreview() {
      if (!this._previewSource || !this._previewCanvas) return;
      const selected = this.colors.find(item => item.name === this.colorway);
      if (!selected) return;
      const rgb = selected.hex.match(/[a-f0-9]{2}/gi).map(value => parseInt(value, 16));
      const output = new ImageData(new Uint8ClampedArray(this._previewSource.data), this._previewSource.width, this._previewSource.height);
      const pixels = output.data;
      const original = this._previewSource.data;
      const width = output.width;
      // Fine, tightly packed oval knot rows. Cache the lighting independently
      // of the yarn colors so every colorway and placement shares the texture.
      if (!this._knotTexture || this._knotTexture.length !== width * output.height) {
        this._knotTexture = new Float32Array(width * output.height);
        const pitchX = width / 310;
        const pitchY = pitchX * .74;
        for (let y = 0; y < output.height; y++) {
          const row = Math.floor(y / pitchY);
          const fy = (y / pitchY) % 1;
          for (let x = 0; x < width; x++) {
            const fx = (x / pitchX + (row % 2) * .5) % 1;
            const crown = Math.sin(Math.PI * fx) * Math.sin(Math.PI * fy);
            const grain = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
            this._knotTexture[y * width + x] = .948 + .085 * crown + .018 * (grain - Math.floor(grain) - .5);
          }
        }
      }
      for (let y = Math.ceil(output.height * .061); y < output.height * .920; y++) {
        for (let x = Math.ceil(width * .060); x < width * .940; x++) {
          const i = (y * width + x) * 4;
          const mask = Math.max(0, Math.min(1, (original[i + 2] - original[i] + 5) / 21));
          // Saturate the white motif in Reverse while retaining a short,
          // antialiased transition at its edge and the original wool texture.
          const edge = Math.max(0, Math.min(1, (mask - .12) / .48));
          const placementMask = edge * edge * (3 - 2 * edge);
          const field = this.direction === 'Original' ? [250, 250, 247] : rgb;
          const motif = this.direction === 'Original' ? rgb : [250, 250, 247];
          const light = (original[i] + original[i + 1] + original[i + 2]) / 3;
          const texture = Math.max(.88, Math.min(1.03, (light + placementMask * 48) / 224)) * this._knotTexture[y * width + x];
          for (let channel = 0; channel < 3; channel++) pixels[i + channel] = (field[channel] * (1 - placementMask) + motif[channel] * placementMask) * texture;
        }
      }
      this._previewCanvas.getContext('2d').putImageData(output, 0, 0);
      this._previewCanvas.setAttribute('aria-label', 'Miramar in ' + this.colorway + ', ' + this.direction.toLowerCase() + ' color placement');
    },

    u_FormatMoney(amount) {
      return formatMoney(amount);
    },
  }));
};
if (window.Alpine) registerMiramar(); else document.addEventListener('alpine:init', registerMiramar, { once: true });

